import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Play, ChevronRight } from "lucide-react";
import { API, BACKEND_URL, COVER_IMAGE } from "../lib/constants";
import { useAudio } from "../contexts/AudioContext";

const SERIES_LABELS = {
  "the-red-ink-chronicles": "The Red Ink Chronicles",
  "signed-sealed-betrayed": "Signed, Sealed, Betrayed",
  "the-notarys-shadow": "The Notary's Shadow",
};

const POSITIONS_KEY = "ris_episode_positions_v1";

export default function ContinueListening() {
  const navigate = useNavigate();
  const { playTrack, getPosition } = useAudio();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let positions = {};
      try { positions = JSON.parse(localStorage.getItem(POSITIONS_KEY) || "{}"); } catch {}
      const sorted = Object.entries(positions)
        .filter(([, v]) => v && v.t > 5)
        .sort((a, b) => (b[1].at || 0) - (a[1].at || 0))
        .slice(0, 5);
      const ids = sorted.map(([id]) => id);
      if (!ids.length) {
        setItems([]);
        setLoading(false);
        return;
      }
      try {
        const res = await axios.post(`${API}/episodes/by-ids`, { ids });
        if (cancelled) return;
        const enriched = res.data.map((ep) => {
          const pos = positions[ep.id]?.t || 0;
          return { ...ep, savedPosition: pos };
        });
        setItems(enriched);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!items.length) return null;

  const handlePlay = (ep) => {
    const audioUrl = ep.mixed_audio_url || ep.audio_url;
    if (!audioUrl) {
      navigate(`/episodes/${ep.id}`);
      return;
    }
    playTrack({
      id: ep.id,
      audio_url: `${BACKEND_URL}${audioUrl}`,
      title: ep.title,
      subtitle: `${SERIES_LABELS[ep.series] || ep.series} · S${ep.season}E${ep.episode_number}`,
      thumbnail_url: ep.thumbnail_url,
      season: ep.season,
      episode_number: ep.episode_number,
      route: `/episodes/${ep.id}`,
    });
  };

  const fmtTime = (s) => {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = String(Math.floor(s % 60)).padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <section className="px-6 mt-6 relative z-20" data-testid="continue-listening-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-cinzel text-sm sm:text-base font-bold tracking-wider text-white flex items-center gap-2">
            <Play size={14} className="text-red-500" /> CONTINUE LISTENING
          </h2>
          <span className="text-xs font-mono text-gray-500">{items.length} EPISODE{items.length !== 1 ? 'S' : ''}</span>
        </div>

        <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-6 px-6 snap-x snap-mandatory scrollbar-thin" data-testid="continue-listening-carousel">
          {items.map((ep) => {
            const cover = ep.thumbnail_url ? `${BACKEND_URL}${ep.thumbnail_url}` : COVER_IMAGE;
            // Don't know duration without playing; show saved time as a "listened so far" marker
            // Build a faux progress: tweak to show "where you stopped" relative to a 600s assumed avg
            const pos = ep.savedPosition || 0;
            // Use a max-clamp at 99% so we always show "almost done" even if we don't know duration
            const fakePct = Math.min(85, (pos / 600) * 100);
            return (
              <div
                key={ep.id}
                className="snap-start flex-shrink-0 w-44 sm:w-56 bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden hover:border-red-500/40 transition-colors group"
                data-testid={`continue-card-${ep.id}`}
              >
                <button
                  onClick={() => handlePlay(ep)}
                  className="block w-full text-left"
                  data-testid={`continue-play-${ep.id}`}
                >
                  <div className="relative aspect-square bg-cover bg-center" style={{ backgroundImage: `url(${cover})` }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-sm text-[10px] font-mono text-white">
                      {fmtTime(pos)}
                    </div>
                    <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play size={14} className="text-white ml-0.5" />
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1 bg-white/5">
                    <div className="h-full bg-red-500" style={{ width: `${fakePct}%` }} />
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] font-mono text-red-400 tracking-wider mb-1 truncate">
                      {SERIES_LABELS[ep.series]?.toUpperCase() || ep.series.toUpperCase()} · S{ep.season}E{String(ep.episode_number).padStart(2,'0')}
                    </p>
                    <p className="text-sm font-cinzel text-white truncate">{ep.title}</p>
                  </div>
                </button>
                <button
                  onClick={() => navigate(`/episodes/${ep.id}`)}
                  className="w-full px-3 py-2 border-t border-white/5 text-[11px] text-gray-400 hover:bg-white/5 hover:text-white flex items-center justify-between transition-colors"
                  data-testid={`continue-details-${ep.id}`}
                >
                  <span>Details</span>
                  <ChevronRight size={12} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
